/**
 * Nayda dog mark — the front-facing variant from the brand guide.
 * `currentColor` drives ink (head + ears); the collar dot is the locked Marigold accent.
 * Size is controlled by parent (Tailwind size classes or inline style on width/height).
 *
 * The artwork is the delivered mark (marketing-accets/nayda.svg) minus its bone
 * background tile: the tile is there so the favicon reads on light/dark browser
 * chrome, but in-page the mark has to sit on whatever surface it's placed on.
 */
export function NaydaMark({ className = '', size = 64 }: { className?: string; size?: number }) {
	return (
		<svg
			className={`text-ink ${className}`}
			width={size}
			height={size}
			viewBox="0 0 452 452"
			aria-hidden
		>
			{/* Ears (drawn first so the head sits over their bases) */}
			<path
				d="M86.7352 158.496C80.1001 161.567 72.3239 156.69 72.2208 149.393L71.1365 72.7181C71.0319 65.3252 78.8575 60.5966 85.5987 63.9792L165.608 104.126C173.145 107.908 173.176 118.489 165.66 121.968L86.7352 158.496Z"
				fill="currentColor"
			/>
			<path
				d="M361.301 158.357C367.929 161.437 375.71 156.575 375.828 149.28L377.068 72.7633C377.189 65.3437 369.321 60.6012 362.569 64.0228L283.208 104.24C275.709 108.04 275.692 118.578 283.179 122.057L361.301 158.357Z"
				fill="currentColor"
			/>
			{/* Head */}
			<path
				d="M293.8 103H158.2C104.113 103 60.2666 146.846 60.2666 200.933V291.333C60.2666 345.42 104.113 389.267 158.2 389.267H293.8C347.887 389.267 391.733 345.42 391.733 291.333V200.933C391.733 146.846 347.887 103 293.8 103Z"
				fill="currentColor"
			/>
			{/* Nose */}
			<path
				d="M236.9 281.434H214.1C204.656 281.434 197 289.252 197 298.897L207 304.718C207 314.362 214.656 322.18 224.1 322.18H226.9C236.344 322.18 244 314.362 244 304.718L254 298.897C254 289.252 246.344 281.434 236.9 281.434Z"
				fill="var(--paper, #FBF8F2)"
			/>
			{/* Muzzle line + confident wide smile */}
			<path
				d="M226 307.019V343.027"
				stroke="var(--paper, #FBF8F2)"
				strokeWidth="7"
				strokeLinecap="round"
				fill="none"
			/>
			<path
				d="M120 290.91C169.075 371.455 284.657 368.612 332 290.91"
				stroke="var(--paper, #FBF8F2)"
				strokeWidth="12"
				strokeLinecap="round"
				fill="none"
			/>
			{/* Accent collar dot — the regal cue */}
			<path
				d="M226 436.135C238.482 436.135 248.6 426.017 248.6 413.535C248.6 401.053 238.482 390.935 226 390.935C213.518 390.935 203.4 401.053 203.4 413.535C203.4 426.017 213.518 436.135 226 436.135Z"
				fill="#E89124"
			/>
			{/* Eyes last, so they read on top of the head */}
			<path
				d="M293.723 246.373C311.439 246.373 325.8 240.434 325.8 233.107C325.8 225.78 311.439 219.841 293.723 219.841C276.007 219.841 261.646 225.78 261.646 233.107C261.646 240.434 276.007 246.373 293.723 246.373Z"
				fill="var(--paper, #FBF8F2)"
			/>
			<path
				d="M159.077 246.373C176.793 246.373 191.155 240.434 191.155 233.107C191.155 225.78 176.793 219.841 159.077 219.841C141.362 219.841 127 225.78 127 233.107C127 240.434 141.362 246.373 159.077 246.373Z"
				fill="var(--paper, #FBF8F2)"
			/>
		</svg>
	)
}

/** Stacked lockup: mark on top, wordmark below. Used on auth screens. */
export function NaydaStacked({ markSize = 80 }: { markSize?: number }) {
	return (
		<div className="flex flex-col items-center gap-3">
			<NaydaMark size={markSize} />
			<span className="font-sans font-semibold tracking-tight text-ink text-[28px]">
				Nayda<span className="text-marigold">.</span>
			</span>
		</div>
	)
}
